import { api } from "./api";

/**
 * The egg grade mix, and what the downgrades cost.
 *
 * The app recorded how MANY eggs and nothing about their quality, so a crate
 * graded down at the buyer's dock reduced realisation with nothing able to say
 * why — the price-realisation report attributed the whole gap to price.
 *
 * See docs/architecture/egg-grading.md.
 */

export interface GradeCount {
  grade: string;
  label: string;
  eggs: number;
  share_pct: number;
  /**
   * False for the three the design discounts. Its PROSE says "cracked/dirty";
   * its CODE discounts floor too and exempts double-yolk — a double yolk is a
   * bigger egg, not a damaged one.
   */
  fetches_full_rate: boolean;
}

export interface EggGradingReport {
  from: string;
  to: string;

  /** The denominator for every share — production on days that were graded. */
  graded_eggs: number;
  grades: GradeCount[];

  /**
   * Production on days nobody graded. Reported rather than folded into table:
   * calling an ungraded day 100% table tells a farm it loses nothing to
   * grading, on the strength of eggs nobody looked at.
   */
  ungraded_eggs: number;
  graded_days: number;
  ungraded_days: number;

  /** Rupees per egg the cost is priced at, and where it came from. */
  table_rate: number;
  table_rate_source: string;
  /** What a downgraded egg fetches as a share of the table rate. */
  cracked_price_factor: number;

  /** discounted eggs × rate × (1 − factor). */
  downgrade_cost: number;
  /** Always true today: egg sales carry no grade, so this is worth, not banked. */
  cost_is_modelled: boolean;
}

export const eggGradingService = {
  report: (from?: string, to?: string) =>
    api
      .get<EggGradingReport>("/egg-grading", { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } })
      .then((r) => r.data),
};
